import {
  type BrokerCookie,
  type ReferralInformationResponse,
} from "@/api/model"
import { getReferralInformation } from "@/api/referral/referral"
import { getCookie, removeCookie, setCookie } from "@/lib/shared/useCookies"
import {
  PAID_CLICK_BROKERS,
  PAID_CLICK_COOKIE_NAME,
  type PaidClick,
  type PaidClickBroker,
} from "@/middleware/paidClick"
import ErrorReporter from "@/services/ErrorReporter"
import axios from "axios"
import { z } from "zod"

import { trackEnrolment } from "@/views/enrolment/enrolmentTracking"

import {
  BROKER_NAME_TO_COOKIE,
  BrokerCookieName,
  JUST_MOVE_IN_COOKIE_VALUE,
  PROMOTIONAL_CODE_USED_COOKIE,
  QueryBrokerName,
  SHORT_EXPIRY_BROKERS,
  type BrokerCookieNameValue,
} from "./brokerConstants"

const ONE_HOUR = 3600
const THIRTY_DAYS = 30 * 24 * 60 * 60

type QueryParams = Record<string, string | null | undefined | (string | null)[]>

const asString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) return value[0] ?? undefined
  return typeof value === "string" ? value : undefined
}

const COOKIE_PRIORITY: BrokerCookieNameValue[] = [
  BrokerCookieName.voucher,
  BrokerCookieName.conectia_reference,
  BrokerCookieName.elmo_customer,
  BrokerCookieName.energy_linx,
  BrokerCookieName.money_super_market,
]

export const getBrokerCookie = (): NonNullable<BrokerCookie> | undefined => {
  for (const cookieName of COOKIE_PRIORITY) {
    const value = getCookie(cookieName)
    if (value) return { name: cookieName, value }
  }
  return undefined
}

export const getBrokerRef = (): string | undefined => {
  const cookie = getBrokerCookie()
  if (!cookie) return undefined

  return cookie.name === BrokerCookieName.conectia_reference
    ? cookie.value
    : undefined
}

const paidClickSchema = z.object({
  broker: z.enum(PAID_CLICK_BROKERS),
  campaign: z.string().optional(),
  campaignId: z.string().optional(),
}) satisfies z.ZodType<PaidClick>

export const getPaidClick = ():
  { broker: PaidClickBroker; campaignReference?: string } | undefined => {
  const parsed = paidClickSchema.safeParse(getCookie(PAID_CLICK_COOKIE_NAME))
  if (!parsed.success) return undefined

  const { broker, campaign, campaignId } = parsed.data
  return {
    broker,
    campaignReference:
      [campaign, campaignId].filter(Boolean).join("|") || undefined,
  }
}

const clearAllBrokerCookies = () => {
  for (const cookieName of Object.values(BrokerCookieName)) {
    removeCookie(cookieName)
  }
}

const getCookieExpiry = (brokerName: string) =>
  SHORT_EXPIRY_BROKERS.has(brokerName) ? ONE_HOUR : THIRTY_DAYS

const getCookieValue = (
  brokerName: string,
  query: QueryParams,
): string | boolean => {
  switch (brokerName) {
    case QueryBrokerName.CONECTIA:
      return asString(query.reqId) ?? ""
    case QueryBrokerName.ELMO:
      return asString(query.id) ?? ""
    case QueryBrokerName.JUST_MOVE_IN:
      return JUST_MOVE_IN_COOKIE_VALUE
    default:
      return true
  }
}

export const processBrokerQueryParams = (query: QueryParams) => {
  const brokerName = asString(query.name)
  if (!brokerName) return

  clearAllBrokerCookies()

  const cookieName = BROKER_NAME_TO_COOKIE[brokerName]

  if (!cookieName) return

  const cookieValue = getCookieValue(brokerName, query)
  const maxAge = getCookieExpiry(brokerName)

  setCookie(cookieName, cookieValue, { maxAge, path: "/" })

  if (brokerName === QueryBrokerName.JUST_MOVE_IN) {
    setCookie(PROMOTIONAL_CODE_USED_COOKIE, "true", { maxAge, path: "/" })
  }
}

export type ReferralResult =
  | ({ status: "valid"; voucherCode: string } & ReferralInformationResponse)
  | { status: "invalid" }

export const processReferralQueryParam = async (
  query: QueryParams,
): Promise<ReferralResult | null> => {
  const referrerCode = asString(query.referrerCode)
  if (!referrerCode || asString(query.name)) return null

  try {
    const referralInfo = await getReferralInformation({
      referrerCode,
    })

    setCookie(BrokerCookieName.voucher, referrerCode, { maxAge: ONE_HOUR })
    removeCookie(PROMOTIONAL_CODE_USED_COOKIE)
    trackEnrolment("voucherInitialCheckSuccessful")

    return { status: "valid", voucherCode: referrerCode, ...referralInfo }
  } catch (error) {
    const isNotFound =
      axios.isAxiosError(error) && error.response?.status === 404
    if (isNotFound) return { status: "invalid" }

    ErrorReporter.report(error)
    return null
  }
}
